import Button from 'atoms/Button/button';
import { StyledSvgSprite } from 'components/general/SVGSprite';
import withErrorBoundary, {
    DashboardModules,
} from 'components/hocs/withErrorBoundary';
import { FlexDiv } from 'components/molecules/utils/basicComponents';
import {
    getCustomScroll,
    getStaticColorScroll,
} from 'components/molecules/utils/customStyles';
import { alpha } from 'foundations/theme';
import { Typography } from 'foundations/typography';
import { motion } from 'framer-motion';
import useLockBodyScroll from 'hooks/useLockBodyScroll';
import React from 'react';
import usePortal from 'react-cool-portal';
import styled, { createGlobalStyle, useTheme } from 'styled-components';

export const AnimateDirection = {
    RIGHT: 'right',
    BOTTOM: 'bottom',
};

const getAnimateValue = (animateDirection) => {
    const { RIGHT, BOTTOM } = AnimateDirection;
    switch (animateDirection) {
        case RIGHT:
            return {
                initial: { opacity: 0, x: '50%' },
                animate: { opacity: 1, x: '0' },
                exit: { opacity: 0, x: '50%' },
            };

        case BOTTOM:
            return {
                initial: { opacity: 0, y: '50%' },
                animate: { opacity: 1, y: '0' },
                exit: { opacity: 0, y: '50%' },
            };
        default:
            return {
                initial: { opacity: 0, x: '50%' },
                animate: { opacity: 1, x: '0' },
                exit: { opacity: 0, x: '50%' },
            };
    }
};

function Drawer({
    onClose,
    isReArrangeSpeakers = false,
    closeIconAlignment = false,
    closeButtonAlignment = false,
    activeLightTheme = false,
    hideCloseActiveBack = false,
    showClose = false,
    showBackButton = false,
    title,
    subtitle,
    subtitleVariant = 'caption',
    subtitleColor = 'ambience.14',
    children,
    footer = null,
    wrapperClosable = false,
    width = '480px',
    zIndex = 1050,
    headerStyle = {},
    footerStyle = {},
    bodyStyle = {},
    titleStyle = {},
    subtitleStyle = {},
    onEditClick,
    onDeleteClick,
    hideDrawerComponents = false,
    headerActions = null,
    showHeader = true,
    portalContainerId = undefined,
    portal = true,
    closeButtonRound = false,
    verticalWidth = '12px',
    backdropFilter = '4px',
    showBackDropFilter = false,
    animateDirection = AnimateDirection.RIGHT,
    height = '100%',
    isStaticColorScroll = true,
    backdropStyles = {},
    hideOverlay = false,
    showSaveBtn = false,
    saveBtnText = 'Save',
    cancelBtnText = 'Cancel',
    onSaveBtnClick,
    isBtnLoading = false,
    showCancelVariant,
    showSaveVariant,
    forceLockBodyScroll = false,
    customCloseRoundButtonStyle,
    closeBtnIconSize = '24px',
    ...rest
}) {
    useLockBodyScroll();
    const theme = useTheme();
    const { initial, animate, exit } = getAnimateValue(animateDirection);

    const { Portal } = usePortal({
        clickOutsideToHide: false,
        escToHide: false,
        containerId: portalContainerId,
    });

    const content = (
        <>
            {hideOverlay ? null : (
                <DrawerOverlay
                    onClick={
                        wrapperClosable && onClose
                            ? () => {
                                  onClose();
                              }
                            : undefined
                    }
                    zIndex={zIndex}
                    hideDrawerComponents={hideDrawerComponents}
                    activeLightTheme={activeLightTheme}
                    style={backdropStyles}
                    showBackDropFilter={showBackDropFilter}
                    backdropFilter={backdropFilter}
                ></DrawerOverlay>
            )}
            <DrawerContainer
                animateDirection={animateDirection}
                key='drawer'
                initial={initial}
                animate={animate}
                exit={exit}
                hideDrawerComponents={hideDrawerComponents}
                transition={{
                    ease: 'easeOut',
                    duration: 0.3,
                }}
                width={width}
                height={height}
                {...rest}
                zIndex={zIndex}
            >
                {showHeader ? (
                    <Header
                        width={width}
                        style={headerStyle || {}}
                        activeLightTheme={activeLightTheme}
                        showBackButton={showBackButton}
                    >
                        <FlexDiv>
                            {showBackButton && (
                                <>
                                    <StyledSvgSprite
                                        icon={'icon-back-arrowlight'}
                                        height={'24px'}
                                        width={'24px'}
                                        fill={
                                            activeLightTheme
                                                ? 'ambience.23'
                                                : 'ambience.0'
                                        }
                                        onClick={onClose}
                                        mr={'26px'}
                                        style={{
                                            cursor: 'pointer',
                                        }}
                                    />
                                </>
                            )}
                            {title && (
                                <Typography
                                    variant={
                                        isReArrangeSpeakers ? 'subtitle1' : 'h6'
                                    }
                                    color={
                                        activeLightTheme
                                            ? 'ambience.23'
                                            : 'ambience.0'
                                    }
                                    style={{ flexGrow: 1, ...titleStyle }}
                                >
                                    {title}
                                </Typography>
                            )}
                            <HeaderActions
                                alignItems='flex-end'
                                showClose={showClose}
                            >
                                {headerActions}
                                {showSaveBtn && (
                                    <SaveBtnContainer>
                                        <StyledButton
                                            variant={
                                                showCancelVariant
                                                    ? showCancelVariant
                                                    : 'primary'
                                            }
                                            onClick={onClose}
                                            closeIconAlignment={
                                                closeIconAlignment
                                            }
                                            minWidth={'84px'}
                                            mr={2}
                                            style={{
                                                backgroundColor:
                                                    theme.colors.ambience[18],
                                            }}
                                        >
                                            {cancelBtnText}
                                        </StyledButton>
                                        <StyledButton
                                            variant={
                                                showSaveVariant
                                                    ? showSaveVariant
                                                    : 'primary'
                                            }
                                            onClick={onSaveBtnClick}
                                            closeIconAlignment={
                                                closeIconAlignment
                                            }
                                            minWidth={'77px'}
                                            loading={isBtnLoading}
                                        >
                                            {saveBtnText}
                                        </StyledButton>
                                    </SaveBtnContainer>
                                )}
                            </HeaderActions>
                        </FlexDiv>
                        {closeIconAlignment && subtitle && (
                            <Typography
                                variant={subtitleVariant}
                                color={subtitleColor}
                                mt={0}
                                style={{
                                    maxWidth: isReArrangeSpeakers
                                        ? '330px'
                                        : '100%',
                                    ...(subtitleStyle || {}),
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                        {showClose && (
                            <ButtonClose
                                variant={'link'}
                                onClick={onClose}
                                closeIconAlignment={closeIconAlignment}
                                isReArrangeSpeakers={isReArrangeSpeakers}
                                closeButtonAlignment={closeButtonAlignment}
                                closeButtonRound={closeButtonRound}
                                customCloseRoundButtonStyle={
                                    customCloseRoundButtonStyle
                                }
                            >
                                <StyledSvgSprite
                                    icon={'icon-times'}
                                    height={closeBtnIconSize}
                                    width={closeBtnIconSize}
                                    fill={
                                        activeLightTheme
                                            ? 'ambience.23'
                                            : 'ambience.0'
                                    }
                                />
                            </ButtonClose>
                        )}
                        {}
                    </Header>
                ) : null}
                {subtitle && !closeIconAlignment && (
                    <Typography
                        variant={'body2'}
                        color={theme.colors.ambience[8]}
                        pt={0}
                        pb={3}
                        px={4}
                        style={subtitleStyle}
                    >
                        {subtitle}
                    </Typography>
                )}
                <Body
                    activeLightTheme={activeLightTheme}
                    footer={footer}
                    style={bodyStyle || {}}
                    verticalWidth={verticalWidth}
                    height={height}
                    isStaticColorScroll={isStaticColorScroll}
                >
                    {children}
                </Body>
                {footer && (
                    <Footer style={footerStyle || {}} width={width}>
                        {footer}
                    </Footer>
                )}
            </DrawerContainer>
            {forceLockBodyScroll && <LockBodyScrollStyle />}
        </>
    );

    return portal ? <Portal>{content}</Portal> : content;
}

export default withErrorBoundary(Drawer, DashboardModules.GENERAL_DRAWER);

const StyledButton = styled(Button)`
    border-radius: 4px;
    height: 36px;
    padding: 5px;
`;

const DrawerOverlay = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: ${({ zIndex }) => zIndex};
    background-color: ${({ activeLightTheme, theme }) =>
        activeLightTheme
            ? alpha(theme.colors.ambience[2], 0.6)
            : alpha(theme.colors.ambience[24], 0.8)};
    ${({ showBackDropFilter, backdropFilter }) =>
        showBackDropFilter &&
        `
        backdrop-filter: blur(${backdropFilter});
    `};
    ${({ hideDrawerComponents }) =>
        hideDrawerComponents &&
        `
        opacity: 0 !important;
    `};
`;
const Header = styled.div`
    max-width: ${({ width }) => width};
    width: 100%;
    display: block;
    padding: 40px 32px 12px 32px;
    background: ${({ theme }) =>
        theme.isLightTheme
            ? theme.colors.ambience[24]
            : theme.colors.ambience[21]};
    ${({ editDeleteIcons }) =>
        editDeleteIcons &&
        `
        display: flex; 
        justify-content: space-between;
    `}
    ${({ showBackButton }) =>
        showBackButton &&
        `
        display: flex; 
        align-items: center;
    `}
`;

const SaveBtnContainer = styled(FlexDiv)``;

const Footer = styled.div`
    max-width: ${({ width }) => width};
    width: 100%;
    display: block;
    padding: 12px 32px 32px 32px;
    background: ${({ theme }) =>
        theme.isLightTheme
            ? theme.colors.ambience[24]
            : theme.colors.ambience[21]};
`;

const Body = styled(FlexDiv)`
    height: ${({ height }) => height};
    overflow: hidden auto;
    padding: 12px 32px ${({ footer }) => (footer ? '12px' : '32px')} 32px;
    flex-grow: 1;

    ${({ activeLightTheme }) =>
        activeLightTheme &&
        `
        padding-top: 0px; 
    `}

    ${({ verticalWidth, theme, isStaticColorScroll }) =>
        isStaticColorScroll
            ? getStaticColorScroll({ verticalWidth })
            : getCustomScroll({ theme, verticalWidth })};
`;
const DrawerContainer = styled(motion.div)`
    max-width: ${({ width }) => width};
    width: 100%;
    position: fixed;
    height: ${({ height }) => height};
    background: ${({ theme }) =>
        theme.isLightTheme
            ? theme.colors.ambience[24]
            : theme.colors.ambience[21]};
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    align-items: stretch;
    text-align: left;
    z-index: ${({ zIndex }) => zIndex};
    ${({ animateDirection }) =>
        animateDirection === AnimateDirection.BOTTOM
            ? `
        border-radius: 24px 24px 0 0;
        bottom:0;
    `
            : `
        right: 0;
        top: 0;
    `};
    ${({ hideDrawerComponents }) =>
        hideDrawerComponents &&
        `
        opacity: 0 !important;
    `};
`;

const ButtonClose = styled(Button)`
    position: absolute;
    top: 24px;
    right: 32px;
    z-index: 1050;
    &:hover {
        opacity: 0.85;
    }
    &:focus {
        outline: 0;
        box-shadow: none;
    }
    ${({ closeIconAlignment }) =>
        closeIconAlignment &&
        `
        top: 44px;
    `}
    ${({ closeButtonAlignment }) =>
        closeButtonAlignment &&
        `
        top: 32px;
    `}
    
    ${({ isReArrangeSpeakers, closeIconAlignment }) =>
        isReArrangeSpeakers &&
        closeIconAlignment &&
        `
        top: 39px;
    `}
    ${({ closeButtonRound, theme }) =>
        closeButtonRound &&
        `
        width: 48px;
        height: 48px;
        border-radius: 100%;
        background: ${alpha(theme.colors.ambience[9], 0.16)};
        top: 32px;
    `}

    ${({ closeButtonRound, customCloseRoundButtonStyle }) =>
        closeButtonRound &&
        customCloseRoundButtonStyle &&
        customCloseRoundButtonStyle}

    ${({ hideCloseActiveBack }) =>
        hideCloseActiveBack &&
        `
        display: flex; 
        align-items: center;
    `}
`;

const HeaderActions = styled(FlexDiv)`
    ${({ showClose }) =>
        showClose &&
        `
        padding-right: 2em;
    `}
`;

const LockBodyScrollStyle = createGlobalStyle`
    body,html {
        overflow: hidden !important;
    }
`;
